'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { Placeholder } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { trackSelectRoomTypeClick } from '@/app/components/hotel-detail/trackInteractionEvents';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useHotelPromotion } from '@/app/components/hotel-detail/hooks/useFetchHotelPromotions';
import { shouldShowStrikeThroughPrice } from '@/app/components/hotel-detail/rooms/roomCommon';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';
import { BottomFixedContainer } from './BottomFixedContainer';
import { PriceSummary } from './PriceSummary';
import { GoToRoomsButton } from './GoToRoomsButton';
import { useUrlSearchParams } from './UrlSearchParamsProvider';

type SubNavigationProps = {
  /** when elements referenced by those refs will become visible the *CTA* gets hidden  */
  hideCta?: boolean;
} & React.HTMLAttributes<HTMLElement>;

type BookCtaProps = {
  hideCta?: boolean;
} & React.HTMLAttributes<HTMLElement>;

const PricePlaceholderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-grow: 1;
`;

function BookCta(props: BookCtaProps) {
  const { t } = useTranslation();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { accommodationOnly } = useUrlSearchParams();
  const { offersData } = useOffersContext();
  const { showLmnDealsPromotion } = useHotelPromotion();
  const cheapestMealPlan = offersData?.cheapestFilteredMealPlan;

  const showLmnDealOnPrice = offersData?.hasFlashSaleOffers && showLmnDealsPromotion;

  const getCTALabel = (currentAccommodationType: AccommodationType | undefined) =>
    currentAccommodationType === 'HOTELS'
      ? t('vacation.select.navigation.cta')
      : t('vacation.select.navigation.cta.accommodation');

  const onCtaClick = () => {
    trackSelectRoomTypeClick();
  };

  return (
    <BottomFixedContainer
      hide={props.hideCta || false}
      css={`
        display: flex;
        justify-content: space-between;
        gap: 8px;
        padding: 16px;
      `}
    >
      {cheapestMealPlan ? (
        <PriceSummary
          mealPlan={cheapestMealPlan}
          showFromLabel={accommodationOnly}
          showLmnDeal={showLmnDealOnPrice}
          showStrikeThroughPrice={
            !accommodationOnly && shouldShowStrikeThroughPrice(cheapestMealPlan, accommodationOnly)
          }
        />
      ) : (
        <PricePlaceholderContainer>
          <Placeholder height="22px" width="80px" />
          <Placeholder height="16px" width="80%" />
        </PricePlaceholderContainer>
      )}

      <GoToRoomsButton
        onClick={onCtaClick}
        css={css`
          font-size: ${({ theme }) => theme.kallisto.font.size['175']};
        `}
      >
        {getCTALabel(hotelStaticData?.accommodationType)}
      </GoToRoomsButton>
    </BottomFixedContainer>
  );
}

export function SubNavigation(props: SubNavigationProps) {
  const { hideCta } = props;
  const { isLoadingRooms, isSoldout } = useOffersContext();
  return !isSoldout && !isLoadingRooms ? <BookCta hideCta={hideCta} /> : undefined;
}
